import React from "react";
import { Controller, Control, FormState, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { FormGroup, Input, Checkbox, Textarea } from "@cofano/cck";

import FormField from "components/cck/FormField";
import ContainerTypeSelect from "view/containertypes/components/selects/ContainerTypeSelect";
import TransportTypeSelect from "components/selects/TransportTypeSelect";
import ProductSelect from "view/products/components/select/ProductSelect";
import UnNumberSelect from "components/selects/UnNumberSelect";
import ReeferCheckTypeSelect from "components/selects/ReeferCheckTypeSelect";
import DeliveryConditionSelect from "view/deliveryconditions/components/selects/DeliveryConditionSelect";
import DatePicker from "components/cck/DatePicker";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import WeightToleranceTypeSelect from "components/selects/WeightToleranceTypeSelect";
import WeightOptionTypeSelect from "components/selects/WeightOptionTypeSelect";

import { Language, usePermission, hasOptionalFieldPermission, hasPluginPermission, Fields } from "imports/Utils";

import { isValidContainerNumber } from "view/containers/utils/container.schema";

import { RelationType, WeightToleranceType } from "__generated__/graphql";

import { CargoForm } from "types/index";

const CargoDetails = ({ calculateWeightAndVolume, setCalculateWeightAndVolume, control, setValue, formState, watch }: {
	calculateWeightAndVolume: boolean,
	setCalculateWeightAndVolume: (value: boolean) => void,
	control: Control<CargoForm>,
	formState: FormState<CargoForm>,
	watch: UseFormWatch<CargoForm>
	setValue: UseFormSetValue<CargoForm>;
}) => {

	const session = useStoreState(state => state.session);

	const { isAdmin, isCompanyUser, isOperator, isTerminal, isMainUserLimited, isCustomer } = usePermission();

	const fieldEnabled = (field: string) => {
		return (
			(isAdmin || isCompanyUser || isOperator || isTerminal || isMainUserLimited || isCustomer) &&
			hasOptionalFieldPermission(session, Fields.ORDER, field)
		);
	};

	const weightToleranceType = watch("weightToleranceType");
	const supplier = watch("supplier");

	return (
		<div>
			{(fieldEnabled("supplier") && !hasPluginPermission(session, "useContainers")) && (
				<FormGroup label={Language("SUPPLIER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="supplier"
						control={control}
						render={({ field }) => (
							<RelationSelect
								{...field}
								types={[ RelationType.Supplier ]}
								onChange={option => {
									field.onChange(option ? { id: option.id, name: option.name } : undefined);

									if (option?.id !== supplier?.id) {
										setValue("product", undefined);
									}
								}} />
						)} />
				</FormGroup>
			)}

			{!hasPluginPermission(session, "useContainers") && (
				<FormGroup
					label={Language("PRODUCT")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<FormField errors={formState.errors} name="product">
						<Controller
							name="product"
							control={control}
							rules={{ required: true }}
							render={({ field }) => <ProductSelect {...field} supplierId={supplier?.id} filterLinkedSupplier={fieldEnabled("supplier")} />} />
					</FormField>
				</FormGroup>
			)}

			{fieldEnabled("containerNumber") && (
				<FormGroup label={Language("CONTAINER_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<FormField errors={formState.errors} validation={intl.formatMessage({ id: "INVALID_CONTAINER_NUMBER" })} name="containerNumber">
						<Controller
							name="containerNumber"
							control={control}
							rules={{
								validate: (value) => isValidContainerNumber(value || ""),
							}}
							render={({ field }) => <Input {...field} />} />
					</FormField>
				</FormGroup>
			)}

			{fieldEnabled("masterBundleContainerNumber") && (
				<FormGroup label={Language("CARGO_MASTER_BUNDLE_CONTAINER_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="masterBundleContainerNumber"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("cargoNumber") && (
				<FormGroup label={Language("CARGO_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="cargoNumber"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("nominationNumber") && (
				<FormGroup label={Language("NOMINATION_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="nominationNumber"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("productDetails") &&
				<FormGroup label={Language("PRODUCT_DETAILS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="productDetails"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			}

			{fieldEnabled("containerOperator") && (
				<FormGroup label={Language("CONTAINER_OPERATOR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="containerOperator"
						control={control}
						render={({ field }) => <RelationSelect {...field} types={[ RelationType.ContainerOperator ]} />} />
				</FormGroup>
			)}

			<FormGroup label={Language("LOADING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="loadingReference"
					control={control}
					render={({ field }) => <Input {...field} />} />
			</FormGroup>

			<FormGroup label={Language("DISCHARGING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="dischargingReference"
					control={control}
					render={({ field }) => <Input {...field} />} />
			</FormGroup>

			{fieldEnabled("containerType") && (
				<FormGroup label={Language("CARGO_CONTAINER_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="containerType"
						control={control}
						render={({ field }) => <ContainerTypeSelect {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("weight") && (
				<FormGroup label={Language("CARGO_WEIGHT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="weight"
						control={control}
						render={({ field }) =>
							<Input
								disabled
								{...field}
								type="number"
								addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />} />
				</FormGroup>
			)}

			{fieldEnabled("volume") && (
				<FormGroup label={Language("CARGO_VOLUME")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="volume"
						control={control}
						render={({ field }) =>
							<Input
								disabled
								{...field}
								type="number"
								addonAfter={intl.formatMessage({ id: "UNIT_M3" })} />} />
				</FormGroup>
			)}

			{fieldEnabled("tareWeight") && (
				<FormGroup label={Language("CARGO_TARE_WEIGHT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="tareWeight"
						control={control}
						render={({ field }) => <Input {...field} type="number" addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />} />
				</FormGroup>
			)}

			{fieldEnabled("grossWeight") && (
				<FormGroup label={Language("CARGO_GROSS_WEIGHT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="grossWeight"
						control={control}
						render={({ field }) => <Input {...field} type="number" addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />} />
				</FormGroup>
			)}

			{fieldEnabled("verifiedGrossWeight") && (
				<FormGroup label={Language("CARGO_VERIFIED_GROSS_WEIGHT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="verifiedGrossWeight"
						control={control}
						render={({ field }) => <Input {...field} type="number" addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />} />
				</FormGroup>
			)}

			{fieldEnabled("weightTolerance") && (
				<FormGroup label={Language("WEIGHT_TOLERANCE_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="weightToleranceType"
						control={control}
						render={({ field }) =>
							<WeightToleranceTypeSelect
								{...field}
								onChange={(type) => {
									if (!type || ![ WeightToleranceType.CharterersOption, WeightToleranceType.OwnersOption ].includes(type)) {
										setValue("weightOptionType", undefined);
										setValue("weightOptionPercentage", undefined);
										setValue("declaredFinalWeight", undefined);
									}
									field.onChange(type);
								}}
							/>}
					/>
				</FormGroup>
			)}
			{weightToleranceType && [ WeightToleranceType.CharterersOption, WeightToleranceType.OwnersOption ].includes(weightToleranceType) && (

				<>
					<FormGroup label={Language("DECLARED_FINAL_WEIGHT")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="declaredFinalWeight"
							control={control}
							render={({ field }) => <Input {...field} type="number" addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />} />
					</FormGroup>

					<FormGroup label={Language("WEIGHT_OPTION_TYPE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="weightOptionType"
							control={control}
							render={({ field }) => <WeightOptionTypeSelect {...field} />} />
					</FormGroup>

					<FormGroup label={Language("WEIGHT_OPTION_PERCENTAGE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="weightOptionPercentage"
							control={control}
							render={({ field }) => <Input {...field} type="number" addonAfter={"%"} />} />
					</FormGroup>
				</>
			)}

			{fieldEnabled("empty") && <FormGroup label={Language("CARGO_EMPTY")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="empty"
					control={control}
					render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
			</FormGroup>}

			{fieldEnabled("desiredTemperature") && (
				<FormGroup label={Language("CARGO_DESIRED_TEMPERATURE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="desiredTemperature"
						control={control}
						render={({ field }) => <Input {...field} type="number" addonAfter={"\u00b0C"} />} />
				</FormGroup>
			)}

			{fieldEnabled("temperatureRangeLower") && (
				<FormGroup label={Language("CARGO_TEMPERATURE_RANGE_LOWER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="temperatureRangeLower"
						control={control}
						render={({ field }) => <Input {...field} type="number" addonAfter={"\u00b0C"} />} />
				</FormGroup>
			)}

			{fieldEnabled("temperatureRangeUpper") && (
				<FormGroup label={Language("CARGO_TEMPERATURE_RANGE_UPPER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="temperatureRangeUpper"
						control={control}
						render={({ field }) => <Input {...field} type="number" addonAfter={"\u00b0C"} />} />
				</FormGroup>
			)}

			{fieldEnabled("sealNumber") && (
				<React.Fragment>
					<FormGroup label={Language("CARGO_ISSEALED")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="sealed"
							control={control}
							render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
					</FormGroup>

					<FormGroup label={Language("CARGO_SEALNUMBER")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="sealNumber"
							control={control}
							render={({ field }) => <Input {...field} />} />
					</FormGroup>
				</React.Fragment>
			)}

			{fieldEnabled("waNumber") && <FormGroup label={Language("WA_NUMBER")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="waNumber"
					control={control}
					render={({ field }) => <Input {...field} />} />
			</FormGroup>}

			{fieldEnabled("bookingReference") && (
				<FormGroup label={Language("CARGO_BOOKING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="bookingReference"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("reference") && (
				<FormGroup label={Language("CARGO_REFERENCE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="reference"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{hasPluginPermission(session, "useContainers") &&
				<>
					<FormGroup label={Language("CARGO_LOADED")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="loaded"
							control={control}
							render={({ field }) => <DatePicker {...field} showTimeInput />} />
					</FormGroup>

					<FormGroup label={Language("CARGO_DISCHARGED")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="discharged"
							control={control}
							render={({ field }) => <DatePicker {...field} showTimeInput />} />
					</FormGroup>
				</>
			}

			{fieldEnabled("transportType") && (
				<FormGroup label={Language("CARGO_TRANSPORT_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="transportType"
						control={control}
						render={({ field }) => <TransportTypeSelect {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("unNumbers") && (
				<FormGroup label={Language("CARGO_UN_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="unNumbers"
						control={control}
						render={({ field }) => <UnNumberSelect {...field} multiple />} />
				</FormGroup>
			)}

			{fieldEnabled("services") && <>
				<FormGroup label={Language("INHIBITED_PRODUCT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isInhibitedProduct"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>

				<FormGroup label={Language("NITROGEN_BLANKETING_REQUIRED")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isNitrogenBlanketingRequired"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>

				<FormGroup label={Language("NITROGEN_PURGING_REQUIRED")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isNitrogenPurgingRequired"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>

				<FormGroup label={Language("TEMPERATURE_CONTROL_REQUIRED")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isTemperatureControlRequired"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>

				<FormGroup label={Language("HOSE_RENTAL")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isHoseRentalRequired"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>

				<FormGroup label={Language("TANK_CLEANING")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isCleaningRequired"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			</>}

			<FormGroup label={Language("WATER_LEVEL_OPTIMISED")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="waterLevelOptimised"
					control={control}
					render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value ?? undefined} />} />
			</FormGroup>

			<FormGroup label={Language("CONVERT_QUANTITIES")} labelPlacement="left-center" overflow="hidden">
				<Checkbox
					onClick={(event) => setCalculateWeightAndVolume(event.currentTarget.checked)}
					checked={calculateWeightAndVolume} />
			</FormGroup>

			{fieldEnabled("reeferCheckType") && (
				<FormGroup label={Language("REEFER_CHECK_TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="reeferCheckType"
						control={control}
						render={({ field }) => <ReeferCheckTypeSelect {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("imoClass") && (
				<FormGroup label={Language("CARGO_IMO_CLASS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="imoClass"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("billOfLading") && (
				<FormGroup label={Language("BL_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="billOfLadingNumber"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("humidity") && (
				<FormGroup label={Language("CARGO_REEFER_HUMIDITY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="humidity"
						control={control}
						render={({ field }) => <Input {...field} type="number" addonAfter={"%"} />} />
				</FormGroup>
			)}

			{fieldEnabled("remarks") && (
				<FormGroup label={Language("CARGO_REMARKS")} labelPlacement="left" overflow="hidden">
					<Controller
						name="remarks"
						control={control}
						render={({ field }) => <Textarea {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("carrierScac") && (
				<FormGroup label={Language("CARGO_CARRIER_SCAC_LOADING")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="scacCode"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("carrierScacDischarging") && (
				<FormGroup label={Language("CARGO_CARRIER_SCAC_DISCHARGING")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="scacCodeDischarging"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("damage") && (
				<FormGroup label={Language("CARGO_DAMAGE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="damage"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("releasedForModality") && (
				<React.Fragment>
					<FormGroup label={Language("CARGO_RELEASED_FOR_BARGE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="releasedForBarge"
							control={control}
							render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
					</FormGroup>

					<FormGroup label={Language("CARGO_RELEASED_FOR_TRAIN")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="releasedForTrain"
							control={control}
							render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
					</FormGroup>

					<FormGroup label={Language("CARGO_RELEASED_FOR_TRUCK")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="releasedForTruck"
							control={control}
							render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
					</FormGroup>
				</React.Fragment>
			)}

			{fieldEnabled("deliveryCondition") && (
				<FormGroup label={Language("DELIVERY_CONDITION")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="deliveryCondition"
						control={control}
						render={({ field }) => <DeliveryConditionSelect {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("revenue") && (
				<FormGroup label={Language("REVENUE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="revenue"
						control={control}
						render={({ field }) => <Input {...field} type="number" />} />
				</FormGroup>
			)}

			{fieldEnabled("connectingVisitLoadingCallReferenceNumber") && (
				<FormGroup label={Language("CONNECTING_VISIT_LOADING_CALL_REFERENCE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="connectingVisitLoadingCallReferenceNumber"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}

			{fieldEnabled("connectingVisitDischargingCallReferenceNumber") && (
				<FormGroup label={Language("CONNECTING_VISIT_DISCHARGING_CALL_REFERENCE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="connectingVisitDischargingCallReferenceNumber"
						control={control}
						render={({ field }) => <Input {...field} />} />
				</FormGroup>
			)}
		</div>
	);
};

export default CargoDetails;
